import "./index.css";

import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import {
  reportClientError,
  setupGlobalErrorReporting,
} from "./application/utils/errorReporter";
import { appConfig } from "./config";
import { AUTH_METHOD_KEY } from "./domain/constants/auth-constants";
import {
  getMsalInstance,
  markMsalRedirectFailed,
} from "./infrastructure/msal/auth-helpers";
import { ErrorBoundary } from "./presentation/components/shared/ErrorBoundary";
import { NotFoundPage } from "./presentation/pages/NotFoundPage";
import { routeTree } from "./routeTree.gen";

setupGlobalErrorReporting();

const router = createRouter({
  routeTree,
  defaultPreload: false,
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
});

// TypeScript registration for type-safe routing
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

async function bootstrap() {
  if (!appConfig.devMockAuth) {
    const instance = getMsalInstance();

    // Purge any stale MSAL sessionStorage entries that used /common (e.g. from a
    // previous deploy where VITE_MSAL_AUTHORITY was undefined). handleRedirectPromise
    // reads the authority from the cached interaction state, not the current config,
    // so a stale /common entry will cause AADSTS50194 even with a correct config.
    const configuredAuthority = appConfig.msalAuthority;
    if (configuredAuthority && !configuredAuthority.includes("/common")) {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith("msal.")) {
          const val = sessionStorage.getItem(key) ?? "";
          if (val.includes("login.microsoftonline.com/common")) {
            sessionStorage.removeItem(key);
          }
        }
      }
    }

    await instance.initialize();

    // Process the Azure AD redirect response BEFORE React/TanStack Router renders.
    // createRouter() runs at module load time and TanStack Router calls
    // history.replaceState() when processing the initial route, which clears
    // window.location.hash before MsalProvider's handleRedirectPromise can read it.
    // Handling it here first ensures the auth code is consumed early.
    // MSAL caches the promise so MsalProvider's second call returns the same result.

    const redirectResult = await instance
      .handleRedirectPromise()
      .catch((error: unknown) => {
        // A cancelled or failed Azure AD login must not prevent the app from rendering.
        reportClientError("auth.msal-redirect", error);
        if (sessionStorage.getItem(AUTH_METHOD_KEY) === "microsoft") {
          sessionStorage.removeItem(AUTH_METHOD_KEY);
        }
        markMsalRedirectFailed();
        return null;
      });

    if (redirectResult?.account) {
      instance.setActiveAccount(redirectResult.account);
    }

    // Set active account from cache if a session already exists.
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0] ?? null);
    }

    // Listen for successful login/redirect to keep active account in sync.
    instance.addEventCallback((event) => {
      if (
        (event.eventType === "msal:loginSuccess" ||
          event.eventType === "msal:acquireTokenSuccess" ||
          event.eventType === "msal:handleRedirectEnd") &&
        event.payload &&
        "account" in event.payload &&
        event.payload.account
      ) {
        instance.setActiveAccount(event.payload.account as never);
      }
    });
  }

  createRoot(rootElement!).render(
    <StrictMode>
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
    </StrictMode>,
  );
}

void bootstrap();
